import styles from './index.module.css'

import React from 'react'

import { FilterComponentProps, FilterOperatorOption } from 'types/filter-page'

import { Flex, Text, TextField } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'

type NumberFormProps = FilterComponentProps & {
  yearOperators?: boolean
}

const NumberForm = ({
  label,
  value,
  onChange,
  operator = 'equals',
  yearOperators = false,
  onPress,
}: NumberFormProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  // Helper function to create a standard number input
  const createNumberInput = (
    inputValue: string,
    onChangeHandler: (_e: React.ChangeEvent<HTMLInputElement>) => void,
    shouldAutoFocus = true,
    hasError = false,
    doubleInput = false,
  ) => (
    <TextField.Root
      value={inputValue}
      onChange={onChangeHandler}
      onKeyDown={handleKeyDown}
      type="number"
      placeholder={label}
      autoFocus={shouldAutoFocus}
      className={[
        styles.numberInput,
        doubleInput ? styles.betweenInput : styles.single,
        hasError ? styles.error : '',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  )

  const allFilterOperators: FilterOperatorOption[] = [
    {
      value: 'equals',
      component: createNumberInput(value || '', e =>
        onChange(e.target.value, operator),
      ),
    },
    {
      value: 'between',
      component: (
        <Flex gap="2" align="center" className={styles.betweenContainer}>
          {createNumberInput(
            value?.[0] || '',
            e => onChange([e.target.value, value?.[1] ?? ''], operator),
            true,
            Array.isArray(value) && !value[0] && !!value[1],
            true,
          )}
          <Text size="2">-</Text>
          {createNumberInput(
            value?.[1] || '',
            e => onChange([value?.[0] ?? '', e.target.value], operator),
            false, // Second input shouldn't auto-focus
            Array.isArray(value) && !!value[0] && !value[1],
            true,
          )}
        </Flex>
      ),
    },
    {
      value: yearOperators ? 'lte' : 'lt',
      component: createNumberInput(value || '', e =>
        onChange(e.target.value, operator),
      ),
    },
    {
      value: yearOperators ? 'gte' : 'gt',
      component: createNumberInput(value || '', e =>
        onChange(e.target.value, operator),
      ),
    },
  ]

  return (
    <FilterOperatorSelector
      operator={operator}
      onChange={onChange}
      operators={allFilterOperators}
    />
  )
}

export default NumberForm
